import type { MapEngine } from "./map-engine.js";
import { LayerPackLoader } from "./layer-pack-loader.js";
import type { HighlightMarkerOptions } from "./layer-packs.js";

/**
 * Embed Host - runs inside the iframe.
 *
 * Receives postMessage commands from the parent SDK and drives the MapEngine.
 * Forwards map events back to the parent.
 *
 * Protocol:
 *   Parent → iframe:
 *     { protocol: "hexmap:", mapId, type: "command", method, params }
 *     { protocol: "hexmap:", mapId, type: "call", method, params, callId }
 *
 *   iframe → Parent:
 *     { protocol: "hexmap:", mapId, type: "ready" }
 *     { protocol: "hexmap:", mapId, type: "event", event, payload }
 *     { protocol: "hexmap:", mapId, type: "response", callId, result?, error? }
 */

const MSG_PREFIX = "hexmap:";

type Handler = (params: Record<string, unknown>) => unknown | Promise<unknown>;

export class EmbedHost {
  private engine: MapEngine;
  private mapId: string;
  private parentOrigin: string;
  private handlers: Record<string, Handler> = {};
  private packLoader: LayerPackLoader;

  constructor(engine: MapEngine) {
    this.engine = engine;
    this.mapId = new URLSearchParams(window.location.search).get("id") || "";
    this.parentOrigin = document.referrer
      ? new URL(document.referrer).origin
      : "*";
    this.packLoader = new LayerPackLoader(engine);

    this.registerDefaultHandlers();
    this.listen();
    const packsReady = this.parseInitialConfig();

    // Signal ready once the engine is initialised AND any requested
    // built-in packs have finished loading. Without this, the parent
    // SDK could send highlightMarker / getMarker before the pack
    // markers exist.
    this.engine.whenReady(() => {
      packsReady.then(() => {
        this.sendToParent({ type: "ready" });
      });
    });

    // Forward engine events to parent
    for (const eventName of [
      "click",
      "moveend",
      "zoomend",
      "markerclick",
      "error",
    ]) {
      this.engine.onEvent(eventName, (payload) => {
        this.emitEvent(eventName, payload);
      });
    }
  }

  // ── Message handling ─────────────────────────────────────────────────

  private listen(): void {
    window.addEventListener("message", (event) => {
      const data = event.data;
      if (!data || data.protocol !== MSG_PREFIX) return;
      if (data.mapId && data.mapId !== this.mapId) return;

      if (data.type === "command") {
        this.handleCommand(data.method, data.params);
      } else if (data.type === "call") {
        this.handleCall(data.method, data.params, data.callId);
      }
    });
  }

  private handleCommand(method: string, params: Record<string, unknown>): void {
    const handler = this.handlers[method];
    if (handler) {
      try {
        handler(params);
      } catch (e) {
        console.error(`[EmbedHost] command "${method}" failed:`, e);
      }
    } else {
      console.warn(`[EmbedHost] unknown command: "${method}"`);
    }
  }

  private async handleCall(
    method: string,
    params: Record<string, unknown>,
    callId: string,
  ): Promise<void> {
    const handler = this.handlers[method];
    if (!handler) {
      this.sendToParent({
        type: "response",
        callId,
        error: `Unknown method: ${method}`,
      });
      return;
    }
    try {
      const result = await handler(params);
      this.sendToParent({ type: "response", callId, result });
    } catch (e) {
      this.sendToParent({
        type: "response",
        callId,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  }

  private sendToParent(msg: Record<string, unknown>): void {
    if (!window.parent || window.parent === window) return;
    msg.protocol = MSG_PREFIX;
    msg.mapId = this.mapId;
    window.parent.postMessage(msg, this.parentOrigin);
  }

  /** Emit an event to the parent SDK. */
  emitEvent(eventName: string, payload: Record<string, unknown>): void {
    this.sendToParent({ type: "event", event: eventName, payload });
  }

  // ── Initial config from URL params ───────────────────────────────────

  /** Parse URL config and return a promise that resolves when packs are loaded. */
  private parseInitialConfig(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const cx = parseFloat(params.get("cx") || "");
    const cy = parseFloat(params.get("cy") || "");
    const z = parseInt(params.get("z") || "", 10);

    if (!isNaN(cx) && !isNaN(cy)) {
      this.engine.setCenter(cx, cy, !isNaN(z) ? z : undefined);
    }

    // Layer visibility: l_towns=1, l_roads=0, etc.
    for (const [key, value] of params) {
      if (key.startsWith("l_")) {
        const layerId = key.slice(2);
        this.engine.setLayerVisibility(layerId, value === "1");
      }
    }

    if (params.get("static") === "1") this.engine.setInteractive(false);
    if (params.get("minimap") === "0") this.engine.showMinimap(false);
    if (params.get("controls") === "0") this.engine.showControls(false);
    if (params.get("theme")) this.engine.setTheme(params.get("theme")!);

    // URL data API: layers, markers, overlays, focus via JSON query param
    const dataParam = params.get("data");
    if (dataParam) {
      try {
        this.applyUrlData(JSON.parse(dataParam));
      } catch (e) {
        console.error("[EmbedHost] Failed to parse 'data' query param:", e);
      }
    }

    // Built-in layer packs — return the promise so ready can wait on it
    return this.initPacks(params);
  }

  /**
   * Load built-in packs based on URL params and embed context.
   *
   * - Standalone (no parent iframe): load all packs by default.
   * - Embedded with `builtinPacks=pack1,pack2`: load listed packs.
   * - Embedded with `builtinPacks=all`: load all packs.
   * - Embedded with `builtinPacks=none`: load nothing.
   * - Embedded with no param: load nothing (SDK opts in via loadPack()).
   */
  private initPacks(params: URLSearchParams): Promise<void> {
    const packsParam = params.get("builtinPacks");
    const isStandalone = !window.parent || window.parent === window;

    if (packsParam === "none") {
      return Promise.resolve();
    }

    if (packsParam === "all" || (!packsParam && isStandalone)) {
      return this.packLoader.loadAllPacks().catch((e) => {
        console.warn("[EmbedHost] Failed to load packs:", e);
      });
    } else if (packsParam) {
      const ids = packsParam.split(",").map((s) => s.trim()).filter(Boolean);
      return this.packLoader.loadPacks(ids).catch((e) => {
        console.warn("[EmbedHost] Failed to load packs:", e);
      });
    }

    return Promise.resolve();
  }

  // ── URL Data API ────────────────────────────────────────────────────

  /**
   * Applies a declarative data payload from the `data` query parameter.
   *
   * Shape:
   *   {
   *     layers: [{
   *       id: string,
   *       visible?: boolean,
   *       opacity?: number,
   *       order?: number,
   *       markers?: MarkerData[],
   *       overlays?: OverlayData[],
   *     }],
   *     focus?: {
   *       x: number,
   *       y: number,
   *       zoom?: number,       // default: 5
   *       marker?: boolean | { icon?, shape?, color?, tooltip?, size? }
   *     }
   *   }
   */
  private applyUrlData(data: Record<string, unknown>): void {
    const e = this.engine;
    const layers = data.layers as Array<Record<string, unknown>> | undefined;

    if (Array.isArray(layers)) {
      for (const layer of layers) {
        const id = layer.id as string;
        if (!id) continue;

        e.createLayer(id, {
          visible: layer.visible as boolean | undefined,
          opacity: layer.opacity as number | undefined,
          order: layer.order as number | undefined,
          minZoom: layer.minZoom as "default" | "low" | number | undefined,
        });

        const markers = layer.markers as
          | Array<Record<string, unknown>>
          | undefined;
        if (Array.isArray(markers)) {
          for (const m of markers) {
            e.addMarker({ ...m, layer: id } as unknown as Parameters<
              typeof e.addMarker
            >[0]);
          }
        }

        const overlays = layer.overlays as
          | Array<Record<string, unknown>>
          | undefined;
        if (Array.isArray(overlays)) {
          for (const o of overlays) {
            e.addOverlay({ ...o, layer: id } as unknown as Parameters<
              typeof e.addOverlay
            >[0]);
          }
        }
      }
    }

    const focus = data.focus as Record<string, unknown> | undefined;
    if (focus && typeof focus.x === "number" && typeof focus.y === "number") {
      const fx = focus.x as number;
      const fy = focus.y as number;
      const fz = (focus.zoom as number) ?? 5;

      // Place a focus marker if requested (true = defaults, or object = custom props)
      if (focus.marker) {
        const layerId = "__focus";
        e.createLayer(layerId, { hidden: true });

        const markerProps =
          typeof focus.marker === "object"
            ? (focus.marker as Record<string, unknown>)
            : {};

        e.addMarker({
          layer: layerId,
          id: "__focus_marker",
          x: fx,
          y: fy,
          shape: "circle",
          icon: "pin",
          color: 0xff5050,
          ...markerProps,
        } as unknown as Parameters<typeof e.addMarker>[0]);
      }

      e.flyTo(fx, fy, { zoom: fz, duration: 0 });
    }
  }

  // ── Handler registration ─────────────────────────────────────────────

  private registerDefaultHandlers(): void {
    const e = this.engine;

    // Navigation
    this.handlers["flyTo"] = (p) =>
      e.flyTo(p.x as number, p.y as number, p as Parameters<typeof e.flyTo>[2]);
    this.handlers["panTo"] = (p) => e.panTo(p.x as number, p.y as number);
    this.handlers["setZoom"] = (p) => e.setZoom(p.zoom as number);
    this.handlers["fitBounds"] = (p) =>
      e.fitBounds(
        p.x1 as number,
        p.y1 as number,
        p.x2 as number,
        p.y2 as number,
        p as Parameters<typeof e.fitBounds>[4],
      );
    this.handlers["setViewportPadding"] = (p) =>
      e.setViewportPadding(p.padding as Parameters<typeof e.setViewportPadding>[0]);
    this.handlers["getView"] = () => e.getView();

    // Layers
    this.handlers["createLayer"] = (p) =>
      e.createLayer(
        p.id as string,
        p as {
          visible?: boolean;
          opacity?: number;
          order?: number;
          label?: string;
          category?: string;
          hidden?: boolean;
          minZoom?: "default" | "low" | number;
        },
      );
    this.handlers["createLayers"] = (p) =>
      e.createLayers(p.items as Parameters<typeof e.createLayers>[0]);
    this.handlers["removeLayer"] = (p) => e.removeLayer(p.id as string);
    this.handlers["clearLayers"] = () => e.clearLayers();
    this.handlers["clearLayer"] = (p) => e.clearLayer(p.id as string);
    this.handlers["setLayerVisibility"] = (p) =>
      e.setLayerVisibility(p.layer as string, p.visible as boolean);
    this.handlers["setLayerOpacity"] = (p) =>
      e.setLayerOpacity(p.layer as string, p.opacity as number);
    this.handlers["getLayers"] = () => e.getLayers();

    // Markers
    this.handlers["addMarker"] = (p) =>
      e.addMarker(p as unknown as Parameters<typeof e.addMarker>[0]);
    this.handlers["updateMarker"] = (p) =>
      e.updateMarker(p.id as string, p as Record<string, unknown>);
    this.handlers["removeMarker"] = (p) => e.removeMarker(p.id as string);
    this.handlers["clearMarkers"] = (p) =>
      e.clearMarkers(p.layer as string | undefined);

    // Overlays
    this.handlers["addOverlay"] = (p) =>
      e.addOverlay(p as unknown as Parameters<typeof e.addOverlay>[0]);
    this.handlers["removeOverlay"] = (p) => e.removeOverlay(p.id as string);
    this.handlers["clearOverlays"] = (p) =>
      e.clearOverlays(p.layer as string | undefined);

    // Heatmaps
    this.handlers["addHeatmap"] = (p) =>
      e.addHeatmap(p as unknown as Parameters<typeof e.addHeatmap>[0]);
    this.handlers["removeHeatmap"] = (p) => e.removeHeatmap(p.id as string);
    this.handlers["clearHeatmaps"] = (p) =>
      e.clearHeatmaps(p.layer as string | undefined);

    // Image overlays
    this.handlers["addImageOverlay"] = (p) =>
      e.addImageOverlay(
        p.layer as string,
        p.imageUrl as string,
        {
          opacity: p.opacity as number | undefined,
          imageGrid: p.imageGrid as { rows: number; cols: number } | undefined,
        },
      );

    // Layer packs
    this.handlers["getAvailablePacks"] = () =>
      this.packLoader.getAvailablePacks();
    this.handlers["loadPack"] = (p) =>
      this.packLoader.loadPack(p.id as string);

    // Marker highlight
    this.handlers["highlightMarker"] = (p) =>
      e.highlightMarker(p.id as string, p as HighlightMarkerOptions);
    this.handlers["getMarker"] = (p) => e.getMarker(p.id as string);
  }

  /** Register a custom command handler (for game-specific extensions). */
  register(method: string, handler: Handler): void {
    this.handlers[method] = handler;
  }
}
